import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { motion } from "framer-motion";
import {
  Ticket,
  Clock,
  CheckCircle,
  TrendingUp,
  AlertCircle,
  Users,
  ArrowUpRight,
  ArrowDownRight,
  Calendar
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from "recharts";

const CHART_COLORS = ["#425B76", "#F5C518", "#10b981", "#f59e0b", "#06b6d4"];

const StatCard = ({ title, value, subtitle, icon: Icon, delay = 0, iconBg, iconColor }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
  >
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{title}</p>
            <p className="text-3xl font-bold text-slate-900 tracking-tight">{value}</p>
            {subtitle && (
              <p className="text-sm text-slate-400 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl ${iconBg || 'bg-[#425B76]/10'} flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${iconColor || 'text-[#425B76]'}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("last_30_days");

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard/stats?period=${period}`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Transform ticket types for pie chart
  const ticketTypesData = stats?.analytics?.ticket_types
    ? Object.entries(stats.analytics.ticket_types).map(([name, value]) => ({
        name: name || "General",
        value
      }))
    : [];

  // Transform daily trend for area chart - show more data for longer periods
  const getDateLabel = (date, currentPeriod) => {
    const d = new Date(date);
    if (currentPeriod === "last_year" || currentPeriod === "all_time") {
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } else if (currentPeriod === "last_quarter") {
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
    return d.toLocaleDateString("en-US", { weekday: "short", day: "numeric" });
  };

  const getSliceCount = (currentPeriod) => {
    switch (currentPeriod) {
      case "last_year":
      case "all_time":
        return 30; // Show last 30 data points
      case "last_quarter":
        return 20;
      case "last_month":
        return 14;
      default:
        return 10; // last_30_days
    }
  };

  const dailyTrendData = stats?.analytics?.daily_trend
    ? Object.entries(stats.analytics.daily_trend)
        .sort(([a], [b]) => a.localeCompare(b))
        .slice(-getSliceCount(period))
        .map(([date, count]) => ({
          date: getDateLabel(date, period),
          fullDate: date,
          tickets: count
        }))
    : [];

  // Priority breakdown data for bar chart
  const priorityData = stats?.analytics?.priority_breakdown
    ? Object.entries(stats.analytics.priority_breakdown).map(([name, value]) => ({
        name,
        count: value
      }))
    : [];

  if (loading) {
    return (
      <Layout title="Dashboard">
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#425B76] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Dashboard">
      <div className="space-y-8">
        {/* Welcome & Time Period Selector */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-1">
              Welcome back, {user?.name?.split(" ")[0]}
            </h2>
            <p className="text-slate-500">
              Here&apos;s what&apos;s happening with your support operations.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-3"
          >
            <span className="text-sm text-slate-500 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Time Period:
            </span>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[180px] bg-white" data-testid="period-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last_30_days">Last 30 Days</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
                <SelectItem value="last_quarter">Last Quarter</SelectItem>
                <SelectItem value="last_year">Last Year</SelectItem>
                <SelectItem value="all_time">All Time</SelectItem>
              </SelectContent>
            </Select>
          </motion.div>
        </div>

        {/* Period Label */}
        {stats?.analytics?.period_label && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm text-slate-500 -mt-4"
          >
            Showing data for: <span className="font-medium text-slate-700">{stats.analytics.period_label}</span>
            {stats.analytics.start_date && (
              <span className="ml-2">
                (from {new Date(stats.analytics.start_date).toLocaleDateString()})
              </span>
            )}
          </motion.div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="stats-grid">
          <StatCard
            title="Total Tickets"
            value={stats?.analytics?.total_tickets || 0}
            icon={Ticket}
            iconBg="bg-[#425B76]/10"
            iconColor="text-[#425B76]"
            trend="12%"
            trendUp={true}
            delay={0}
          />
          <StatCard
            title="Open Tickets"
            value={stats?.analytics?.open_tickets || 0}
            subtitle="Awaiting response"
            icon={AlertCircle}
            iconBg="bg-amber-100"
            iconColor="text-amber-600"
            delay={0.1}
          />
          <StatCard
            title="Avg Resolution"
            value={`${stats?.analytics?.avg_resolution_time || 0}h`}
            subtitle="Time to close"
            icon={Clock}
            iconBg="bg-cyan-100"
            iconColor="text-cyan-600"
            delay={0.2}
          />
          <StatCard
            title="CSAT Score"
            value={`${stats?.csat_score || 0}%`}
            subtitle="Customer satisfaction"
            icon={CheckCircle}
            iconBg="bg-emerald-100"
            iconColor="text-emerald-600"
            delay={0.3}
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Ticket Trend */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900">
                  Ticket Volume Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  {dailyTrendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={dailyTrendData}>
                        <defs>
                          <linearGradient id="ticketGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#425B76" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#425B76" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="4" stroke="#e2e8f0" />
                        <XAxis
                          dataKey="date"
                          stroke="#94a3b8"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="#94a3b8"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
                          }}
                          labelStyle={{ color: "#64748b" }}
                          itemStyle={{ color: "#0f172a" }}
                        />
                        <Area
                          type="monotone"
                          dataKey="tickets"
                          stroke="#425B76"
                          strokeWidth={2}
                          fill="url(#ticketGradient)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-400">
                      No trend data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Ticket Types */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900">
                  Tickets by Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  {ticketTypesData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={ticketTypesData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={4}
                          dataKey="value"
                        >
                          {ticketTypesData.map((entry, index) => (
                            <Cell
                              key={`cell-${entry.name ?? index}`}
                              fill={CHART_COLORS[index % CHART_COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-400">
                      No ticket type data available
                    </div>
                  )}
                </div>
                {/* Legend */}
                <div className="flex flex-wrap gap-4 mt-4 justify-center">
                  {ticketTypesData.map((entry, index) => (
                    <div key={entry.name} className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                      />
                      <span className="text-sm text-slate-600">{entry.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Team Size */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card className="bg-white border-slate-200 shadow-sm h-full">
              <CardContent className="p-6 flex flex-col justify-center h-full">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center">
                    <Users className="w-7 h-7 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-4xl font-bold text-slate-900">{stats?.team_size || 0}</p>
                    <p className="text-slate-500">Team Members</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* NPS Score */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Card className="bg-white border-slate-200 shadow-sm h-full">
              <CardContent className="p-6 flex flex-col justify-center h-full">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="w-7 h-7 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-4xl font-bold text-slate-900">{stats?.nps_score || 0}</p>
                    <p className="text-slate-500">NPS Score</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Resolved */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <Card className="bg-white border-slate-200 shadow-sm h-full">
              <CardContent className="p-6 flex flex-col justify-center h-full">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-cyan-100 flex items-center justify-center">
                    <CheckCircle className="w-7 h-7 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-4xl font-bold text-slate-900">{stats?.analytics?.resolved_tickets || 0}</p>
                    <p className="text-slate-500">Resolved Today</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
